import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Sparkles, FileText, Loader2, Maximize2, Minimize2 } from 'lucide-react';
import { Language } from '../types';
import { callExplainSource, SourceConversationTurn } from '../services/geminiService';
import { parseMarkdown } from '../utils/markdownUtils';

export interface SourceContext {
    boardTitle?: string;
    pdfName?: string;
    pageNumber?: number;
    selectedNote?: { title: string; content: string };
    recentNotesTitles?: string[];
}

interface AskAIModalProps {
    isOpen: boolean;
    onClose: () => void;
    imageBase64: string | null;
    language: Language;
    context?: SourceContext;
    onCreateNote: (imageBase64: string) => void;
}

const MAX_USER_TURNS = 3;

const QUICK_PROMPTS: Record<Language, string[]> = {
    english: [
        'Explain briefly',
        'Explain step by step',
        'What is the main idea?',
        'What background do I need?',
        'Give me a simple example',
    ],
    vietnamese: [
        'Giải thích ngắn gọn',
        'Giải thích từng bước',
        'Ý chính là gì?',
        'Cần biết nền gì?',
        'Cho ví dụ đơn giản',
    ],
};

const LABELS: Record<Language, {
    title: string; subtitle: string; inputPlaceholder: string; send: string;
    createNote: string; nudge: string; maxTurns: string; support: string;
    errorGeneric: string; errorQuota: string; loading: string;
}> = {
    english: {
        title: 'Ask AI about this source',
        subtitle: 'Select a quick prompt or type your question',
        inputPlaceholder: 'Ask anything about this excerpt…',
        send: 'Send',
        createNote: 'Create note from this source',
        nudge: 'Now turn this into your own explanation.',
        maxTurns: "You've reached the question limit. Create a note to continue learning.",
        support: 'Use this as support, not as your final note.',
        errorGeneric: 'Something went wrong. Please try again.',
        errorQuota: 'Daily limit reached. Upgrade to continue using AI features.',
        loading: 'Thinking…',
    },
    vietnamese: {
        title: 'Hỏi AI về đoạn này',
        subtitle: 'Chọn câu hỏi gợi ý hoặc tự nhập',
        inputPlaceholder: 'Hỏi bất kỳ điều gì về đoạn này…',
        send: 'Gửi',
        createNote: 'Tạo ghi chú từ đoạn này',
        nudge: 'Bây giờ hãy viết lại bằng lời của bạn.',
        maxTurns: 'Bạn đã đạt giới hạn câu hỏi. Tạo ghi chú để tiếp tục học.',
        support: 'Hãy dùng phần này để hỗ trợ hiểu, không phải để thay cho ghi chú của bạn.',
        errorGeneric: 'Có lỗi xảy ra. Vui lòng thử lại.',
        errorQuota: 'Đã hết lượt hôm nay. Nâng cấp để tiếp tục dùng tính năng AI.',
        loading: 'Đang suy nghĩ…',
    },
};

export const AskAIModal: React.FC<AskAIModalProps> = ({
    isOpen, onClose, imageBase64, language, context, onCreateNote,
}) => {
    const [conversation, setConversation] = useState<SourceConversationTurn[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const conversationEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const t = LABELS[language];
    const quickPrompts = QUICK_PROMPTS[language];
    const userTurnCount = conversation.filter(m => m.role === 'user').length;
    const atMaxTurns = userTurnCount >= MAX_USER_TURNS;
    const hasAnyResponse = conversation.some(m => m.role === 'model');

    useEffect(() => {
        if (!isOpen) {
            setConversation([]);
            setInputValue('');
            setError(null);
            setIsLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (conversation.length > 0) {
            conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [conversation, isLoading]);

    const handleSend = async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed || isLoading || atMaxTurns || !imageBase64) return;

        const historyBeforeSend = [...conversation];
        const userTurn: SourceConversationTurn = { role: 'user', text: trimmed };
        setConversation(prev => [...prev, userTurn]);
        setInputValue('');
        setError(null);
        setIsLoading(true);

        try {
            const response = await callExplainSource(imageBase64, trimmed, historyBeforeSend, language, context);
            setConversation(prev => [...prev, { role: 'model', text: response }]);
        } catch (e: any) {
            const isQuota = e.message?.includes('Hết lượt') || e.message?.includes('resource-exhausted') || e.message?.includes('Daily limit');
            setError(isQuota ? t.errorQuota : t.errorGeneric);
            setConversation(prev => prev.slice(0, -1));
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend(inputValue);
        }
    };

    if (!isOpen || !imageBase64) return null;

    return (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className={`bg-white dark:bg-slate-900 w-full rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 transition-all ${isExpanded ? 'max-w-4xl' : 'max-w-lg'}`}
                style={{ maxHeight: '90vh' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-4 border-b dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-slate-950 shrink-0">
                    <div className="flex items-center gap-2">
                        <Sparkles size={18} className="text-violet-500" />
                        <h3 className="font-bold text-base text-gray-900 dark:text-white">{t.title}</h3>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setIsExpanded(v => !v)}
                            className="p-2 hover:bg-gray-200 dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500"
                            title={isExpanded ? 'Thu nhỏ' : 'Mở rộng'}
                        >
                            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-200 dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body: stack vertically in normal mode, side-by-side in expanded mode */}
                <div className={`flex min-h-0 flex-1 overflow-hidden ${isExpanded ? 'flex-row' : 'flex-col'}`}>

                {/* Image + context column */}
                <div className={`shrink-0 ${isExpanded ? 'w-80 border-r border-gray-100 dark:border-slate-800 overflow-y-auto p-4 flex flex-col gap-3' : 'px-4 pt-4'}`}>
                    <div className={`rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 flex items-center justify-center ${isExpanded ? 'max-h-64' : 'max-h-36'}`}>
                        <img src={imageBase64} alt="Source excerpt" className={`object-contain ${isExpanded ? 'max-h-64' : 'max-h-36'}`} />
                    </div>
                    {/* Context indicator */}
                    {context && (context.boardTitle || context.pdfName || context.pageNumber) && (
                        <div className={`flex flex-wrap gap-1.5 ${isExpanded ? '' : 'mt-2'}`}>
                            {context.pdfName && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                                    <FileText size={9} /> {context.pdfName.length > 28 ? context.pdfName.slice(0, 28) + '…' : context.pdfName}
                                    {context.pageNumber ? ` · p.${context.pageNumber}` : ''}
                                </span>
                            )}
                            {context.boardTitle && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-800">
                                    {context.boardTitle.length > 24 ? context.boardTitle.slice(0, 24) + '…' : context.boardTitle}
                                </span>
                            )}
                            {context.selectedNote?.title && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800">
                                    Note: {context.selectedNote.title.length > 20 ? context.selectedNote.title.slice(0, 20) + '…' : context.selectedNote.title}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Right column: conversation + input + CTA */}
                <div className="flex flex-col flex-1 min-h-0">

                {/* Conversation area */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 min-h-0">
                    {conversation.length === 0 && !isLoading && (
                        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-2">{t.subtitle}</p>
                    )}

                    {conversation.map((msg, i) => (
                        msg.role === 'user' ? (
                            /* User question — compact label, no bubble */
                            <div key={i} className="flex items-start gap-2">
                                <span className="shrink-0 mt-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-500 bg-violet-50 dark:bg-violet-900/20 px-1.5 py-0.5 rounded">
                                    {language === 'vietnamese' ? 'Bạn' : 'You'}
                                </span>
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{msg.text}</p>
                            </div>
                        ) : (
                            /* AI response — full width, document style */
                            <div key={i} className="w-full border-t border-gray-100 dark:border-slate-800 pt-3">
                                <div
                                    className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed [&>p]:my-1.5 [&>ul]:my-1.5 [&>ul]:pl-4 [&>ul]:list-disc [&>ol]:my-1.5 [&>ol]:pl-4 [&>ol]:list-decimal [&_strong]:font-semibold [&_h3]:font-bold [&_h3]:text-base [&_h3]:mt-2 [&_h3]:mb-1 [&_hr]:my-3 [&_hr]:border-gray-200 [&_hr]:dark:border-slate-700"
                                    dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text) }}
                                />
                                <p className="text-xs text-gray-400 dark:text-gray-500 italic mt-2">{t.support}</p>
                            </div>
                        )
                    ))}

                    {isLoading && (
                        <div className="w-full border-t border-gray-100 dark:border-slate-800 pt-3 flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
                            <Loader2 size={14} className="animate-spin" />
                            {t.loading}
                        </div>
                    )}

                    {error && (
                        <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3.5 py-2.5 text-sm text-red-700 dark:text-red-400">
                            {error}
                        </div>
                    )}

                    <div ref={conversationEndRef} />
                </div>

                {/* Quick prompts — shown only before first AI response */}
                {!hasAnyResponse && !isLoading && (
                    <div className="px-4 pb-2 shrink-0">
                        <div className="flex flex-wrap gap-1.5">
                            {quickPrompts.map(prompt => (
                                <button
                                    key={prompt}
                                    onClick={() => handleSend(prompt)}
                                    disabled={isLoading}
                                    className="px-3 py-1.5 text-xs rounded-full border border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors disabled:opacity-50"
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Input area */}
                <div className="px-4 pb-3 shrink-0">
                    {atMaxTurns ? (
                        <p className="text-sm text-center text-amber-600 dark:text-amber-400 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-xl px-3">
                            {t.maxTurns}
                        </p>
                    ) : (
                        <div className="flex gap-2 items-end">
                            <textarea
                                ref={inputRef}
                                value={inputValue}
                                onChange={e => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t.inputPlaceholder}
                                disabled={isLoading}
                                rows={1}
                                className="flex-1 resize-none rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50 min-h-[40px] max-h-24 overflow-y-auto"
                                style={{ height: 'auto' }}
                                onInput={e => {
                                    const el = e.currentTarget;
                                    el.style.height = 'auto';
                                    el.style.height = Math.min(el.scrollHeight, 96) + 'px';
                                }}
                            />
                            <button
                                onClick={() => handleSend(inputValue)}
                                disabled={!inputValue.trim() || isLoading}
                                className="p-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                            >
                                <Send size={16} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Create note CTA */}
                <div className="px-4 pb-4 shrink-0 border-t dark:border-slate-800 pt-3">
                    <button
                        onClick={() => { onCreateNote(imageBase64); onClose(); }}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-medium text-sm transition-colors"
                    >
                        <FileText size={16} />
                        {t.createNote}
                    </button>
                    <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-2 italic">{t.nudge}</p>
                </div>
                </div>{/* end right column */}
                </div>{/* end body wrapper */}
            </div>
        </div>
    );
};
